import { useQueryClient } from '@tanstack/react-query';
import { v4 as uuid } from 'uuid';
import { api } from 'src/common/utils/api';

/**
 * Raised only when part of the item landed. Nothing landing at all is an ordinary failure: the
 * adviser has lost nothing but the keystrokes, and telling them it "saved" would be a lie.
 */
export class PartialSaveError extends Error {
  constructor(
    public readonly failedSaveClientIds: string[],
    public readonly failedDeleteClientIds: string[],
  ) {
    super('Some family members could not be saved');
    this.name = 'PartialSaveError';
  }
}

type Submission<TAllocation extends { clientId: string; itemId?: string }> = {
  allocations: TAllocation[];
  groupId?: string;
  removedItemIds: { clientId: string; itemId: string }[];
};

type Resource = {
  /** Path segment and query key, e.g. "incomes". */
  collection: string;
  queryKey: string;
};

type Operation = {
  kind: 'save' | 'delete';
  clientId: string;
  run: () => Promise<unknown>;
};

/**
 * An item several members share is stored as one row per member, each on that member's own
 * client, tied by a shared group id. Both the batch upsert and the delete endpoint are scoped
 * to a single client, so saving is one request per member touched; a request that fails leaves
 * the others saved, which the caller is told about by name rather than being left to assume the
 * whole item landed.
 */
export function useSaveGroupedItem<
  TAllocation extends { clientId: string; itemId?: string },
  TSubmission extends Submission<TAllocation>,
>(
  resource: Resource,
  toBody: (
    allocation: TAllocation,
    submission: TSubmission,
    groupId: string | undefined,
  ) => Record<string, unknown>,
): (submission: TSubmission) => Promise<void> {
  const queryClient = useQueryClient();

  return async submission => {
    // An item reaching two members needs a group id to be read back as one; keep the one it
    // already has so editing never splits an item into two.
    const groupId =
      submission.allocations.length > 1 ? (submission.groupId ?? uuid()) : submission.groupId;

    const operations: Operation[] = [
      ...submission.allocations.map(allocation => ({
        kind: 'save' as const,
        clientId: allocation.clientId,
        run: () =>
          api.put(`clients/${allocation.clientId}/${resource.collection}/batch`, [
            toBody(allocation, submission, groupId),
          ]),
      })),
      ...submission.removedItemIds.map(removed => ({
        kind: 'delete' as const,
        clientId: removed.clientId,
        run: () =>
          api.post(`clients/${removed.clientId}/${resource.collection}/delete-request`, [
            removed.itemId,
          ]),
      })),
    ];

    await runOperations(operations, resource, queryClient);
  };
}

/**
 * Deleting the whole item means deleting every member's copy of it, so it runs the same per-client
 * fan-out as a save and reports a partial failure the same way: an item the adviser was told was
 * gone must not still be sitting against a member they were never named.
 */
export function useDeleteGroupedItem(
  resource: Resource,
): (items: { id: string; clientId: string }[]) => Promise<void> {
  const queryClient = useQueryClient();

  return async items => {
    const operations: Operation[] = items.map(item => ({
      kind: 'delete' as const,
      clientId: item.clientId,
      run: () =>
        api.post(`clients/${item.clientId}/${resource.collection}/delete-request`, [item.id]),
    }));

    await runOperations(operations, resource, queryClient);
  };
}

async function runOperations(
  operations: Operation[],
  resource: Resource,
  queryClient: ReturnType<typeof useQueryClient>,
): Promise<void> {
  const results = await Promise.allSettled(operations.map(operation => operation.run()));

  const touchedClientIds = [...new Set(operations.map(operation => operation.clientId))];
  await Promise.all(
    touchedClientIds.map(clientId =>
      queryClient.invalidateQueries({ queryKey: ['clients', clientId, resource.queryKey] }),
    ),
  );

  const failed = operations.filter((_operation, index) => results[index].status === 'rejected');
  if (failed.length === 0) return;

  if (failed.length === operations.length) {
    throw new Error(`Every ${resource.collection} request failed`);
  }

  throw new PartialSaveError(clientIdsOf(failed, 'save'), clientIdsOf(failed, 'delete'));
}

function clientIdsOf(operations: Operation[], kind: Operation['kind']): string[] {
  return [
    ...new Set(
      operations.filter(operation => operation.kind === kind).map(operation => operation.clientId),
    ),
  ];
}
